import React from 'react';
import { 
  getDaysUntil, 
  formatCountdown, 
  getUpcomingEvents, 
  getTodayEvents, 
  getThisMonthEvents,
  getMilestoneEvents,
  getOnThisDayEvents,
  generateCalendarStats,
  formatDisplayDate,
  getOrdinalSuffix,
  downloadICalFile
} from '../utils/calendarUtils';

const CalendarDashboard = ({ events, gedcomData }) => {
  // Export feature toggle - can be disabled for future consideration
  const EXPORT_FEATURE_ENABLED = false; // Toggle to enable/disable export functionality
  
  if (!events || events.length === 0) {
    return (
      <div className="calendar-dashboard">
        <div className="dashboard-loading">
          <p>Loading family calendar data...</p>
        </div>
      </div>
    );
  }

  const upcomingEvents = getUpcomingEvents(events, 30);
  const todayEvents = getTodayEvents(events);
  const thisMonthEvents = getThisMonthEvents(events);
  const milestoneEvents = getMilestoneEvents(events);
  const onThisDayEvents = getOnThisDayEvents(events);
  const stats = generateCalendarStats(events, gedcomData);

  const handleExportUpcoming = () => {
    downloadICalFile(upcomingEvents, 'upcoming-family-events.ics', 'Upcoming Family Events');
  };

  const handleExportThisMonth = () => {
    downloadICalFile(thisMonthEvents, 'this-month-family-events.ics', 'This Month\'s Family Events');
  };

  const handleExportAll = () => {
    downloadICalFile(events, 'complete-family-calendar.ics', 'Complete Family Calendar');
  };

  return (
    <div className="calendar-dashboard container">

      {/* Today's Events */}
      {todayEvents.length > 0 && (
        <div className="dashboard-section today-section">
          <h3>
            <i className="fas fa-star"></i>
            Today's Special Days
          </h3>
          <div className="today-events">
            {todayEvents.map(event => (
              <div key={event.id} className={`today-event ${event.type}`}>
                <div className="event-icon">{event.icon}</div>
                <div className="event-content">
                  <h4>{event.person}</h4>
                  <p>{event.description}</p>
                  {event.yearsAgo && (
                    <span className="milestone-badge">
                      {event.yearsAgo}{getOrdinalSuffix(event.yearsAgo)} Anniversary
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Countdown Timers */}
      <div className="dashboard-section countdown-section">
        <h3>
          <i className="fas fa-clock"></i>
          Upcoming Celebrations
        </h3>
        {upcomingEvents.length > 0 ? (
          <div className="countdown-grid">
            {upcomingEvents.slice(0, 6).map(event => {
              const daysUntil = getDaysUntil(event.date);
              const isMilestone = milestoneEvents.some(m => m.id === event.id);
              
              return (
                <div key={event.id} className={`countdown-card ${event.type} ${isMilestone ? 'milestone' : ''}`}>
                  <div className="countdown-header">
                    <div className="event-icon">{event.icon}</div>
                    <div className="countdown-timer">
                      <div className="countdown-number">{Math.abs(daysUntil)}</div>
                      <div className="countdown-unit">
                        {daysUntil === 0 ? 'Today!' : 
                         daysUntil === 1 ? 'Day' : 'Days'}
                      </div>
                    </div>
                  </div>
                  <div className="countdown-content">
                    <h4>{event.person}</h4>
                    <p className="countdown-description">{event.description}</p>
                    <div className="countdown-date">
                      {formatDisplayDate(event.date, { weekday: 'short', month: 'short', day: 'numeric' })}
                    </div>
                    {isMilestone && (
                      <div className="milestone-indicator">
                        <i className="fas fa-trophy"></i>
                        Milestone!
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-upcoming">
            <p>No upcoming events in the next 30 days.</p>
          </div>
        )}
      </div>

      {/* This Month's Events */}
      {thisMonthEvents.length > 0 && (
        <div className="dashboard-section month-section">
          <h3>
            <i className="fas fa-calendar-alt"></i>
            This Month's Celebrations ({thisMonthEvents.length})
          </h3>
          <div className="month-events">
            {thisMonthEvents.map(event => (
              <div key={event.id} className={`month-event ${event.type}`}>
                <div className="event-date">
                  <div className="date-number">{new Date(event.date).getDate()}</div>
                  <div className="date-day">
                    {formatDisplayDate(event.date, { weekday: 'short' })}
                  </div>
                </div>
                <div className="event-icon">{event.icon}</div>
                <div className="event-details">
                  <h4>{event.person}</h4>
                  <p>{event.description}</p>
                  {event.yearsAgo && (
                    <span className="years-badge">{event.yearsAgo} years</span>
                  )}
                </div>
                <div className="countdown-mini">
                  {formatCountdown(getDaysUntil(event.date))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* On This Day in History */}
      {onThisDayEvents.length > 0 && (
        <div className="dashboard-section history-section">
          <h3>
            <i className="fas fa-history"></i>
            On This Day in Family History
          </h3>
          <div className="history-events">
            {onThisDayEvents.map(event => {
              const yearsAgo = new Date().getFullYear() - new Date(event.date).getFullYear();
              return (
                <div key={event.id} className={`history-event ${event.type}`}>
                  <div className="history-year">{new Date(event.date).getFullYear()}</div>
                  <div className="event-icon">{event.icon}</div>
                  <div className="history-content">
                    <h4>{event.person}</h4>
                    <p>{event.description}</p>
                    <span className="years-ago">{yearsAgo} years ago</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="dashboard-section stats-section">
        <h3>
          <i className="fas fa-chart-bar"></i>
          Family Calendar Statistics
        </h3>
        <div className="stats-grid">
          <div className="stat-card calendar-stat-card-fix">
            <div className="stat-number">{stats.upcomingCount}</div>
            <div className="stat-label">Upcoming Events</div>
            <div className="stat-sublabel">Next 30 days</div>
          </div>
          <div className="stat-card calendar-stat-card-fix">
            <div className="stat-number">{stats.thisMonthCount}</div>
            <div className="stat-label">This Month</div>
            <div className="stat-sublabel">Celebrations</div>
          </div>
          <div className="stat-card calendar-stat-card-fix">
            <div className="stat-number">{stats.milestonesCount}</div>
            <div className="stat-label">Milestones</div>
            <div className="stat-sublabel">Special anniversaries</div>
          </div>
          <div className="stat-card calendar-stat-card-fix">
            <div className="stat-number">{stats.totalFamilyMembers}</div>
            <div className="stat-label">Family Members</div>
            <div className="stat-sublabel">In our tree</div>
          </div>
          <div className="stat-card calendar-stat-card-fix">
            <div className="stat-number">{stats.yearsOfHistory}+</div>
            <div className="stat-label">Years of History</div>
            <div className="stat-sublabel">Since 1866</div>
          </div>
          <div className="stat-card calendar-stat-card-fix">
            <div className="stat-number">{stats.totalEvents}</div>
            <div className="stat-label">Total Events</div>
            <div className="stat-sublabel">All celebrations</div>
          </div>
        </div>
      </div>

      {/* Export Buttons - Moved to Bottom */}
      {EXPORT_FEATURE_ENABLED && (
        <div className="dashboard-section export-section">
          <h3>
            <i className="fas fa-download"></i>
            Export to Your Calendar
          </h3>
          <p>Add family events to your personal calendar app (Google Calendar, Apple Calendar, Outlook, etc.)</p>
          <div className="export-buttons">
            <button className="export-btn primary" onClick={handleExportUpcoming}>
              <i className="fas fa-calendar-plus"></i>
              Export Upcoming Events ({upcomingEvents.length})
            </button>
            <button className="export-btn secondary" onClick={handleExportThisMonth}>
              <i className="fas fa-calendar-alt"></i>
              Export This Month ({thisMonthEvents.length})
            </button>
            <button className="export-btn tertiary" onClick={handleExportAll}>
              <i className="fas fa-calendar"></i>
              Export All Events ({events.length})
            </button>
          </div>
          <div className="export-help">
            <p><strong>How to use:</strong> Click a button to download a calendar file, then open it with your calendar app to add the events.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarDashboard;